import { useEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type PointerEvent } from 'react';
import { Button } from '../Button';
import { Input } from '../Input';
import { Icon } from '../Icon';
import { Popover } from '../Popover';
import { clamp, hexToHsv, hsvToHex, normalizeHex, type Hsv } from './color';
import styles from './ColorPicker.module.css';

interface EyeDropperInstance { open: (options?: { signal?: AbortSignal }) => Promise<{ sRGBHex: string }> }
type EyeDropperConstructor = new () => EyeDropperInstance;
export interface ColorPickerProps {
  ariaLabel: string;
  value: string;
  onValueChange: (value: string) => void;
  /** 确定或关闭面板，仅在当前选色会话发生变化时提交一次。 */
  onCommit?: (value: string) => void;
  disabled?: boolean;
  className?: string;
}

function ColorPanel({ value, label, change, close, picking }: { value: string; label: string; change: (value: string) => void; close: () => void; picking: (active: boolean) => void }) {
  const [hsv, setHsv] = useState(() => hexToHsv(value));
  const [hex, setHex] = useState(value.toUpperCase());
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const task = useRef<AbortController | null>(null);
  const current = useRef(hsv);
  const EyeDropper = (window as Window & { EyeDropper?: EyeDropperConstructor }).EyeDropper;
  useEffect(() => () => task.current?.abort(), []);
  function update(next: Hsv) {
    current.current = next; setHsv(next);
    const color = hsvToHex(next); setHex(color); setError(''); change(color);
  }
  function point(event: PointerEvent<HTMLDivElement>, hue: boolean) {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = clamp((event.clientX - rect.left) / rect.width, 0, 1);
    const y = clamp((event.clientY - rect.top) / rect.height, 0, 1);
    update(hue ? { ...current.current, h: y * 360 } : { ...current.current, s: x * 100, v: (1 - y) * 100 });
  }
  function keyboard(event: KeyboardEvent<HTMLDivElement>, hue: boolean) {
    const step = event.shiftKey ? 10 : 1;
    const next = { ...current.current };
    if (hue) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowRight') next.h = clamp(next.h + step, 0, 360);
      else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') next.h = clamp(next.h - step, 0, 360);
      else if (event.key === 'Home') next.h = 0;
      else if (event.key === 'End') next.h = 360;
      else return;
    } else {
      if (event.key === 'ArrowRight') next.s = clamp(next.s + step, 0, 100);
      else if (event.key === 'ArrowLeft') next.s = clamp(next.s - step, 0, 100);
      else if (event.key === 'ArrowUp') next.v = clamp(next.v + step, 0, 100);
      else if (event.key === 'ArrowDown') next.v = clamp(next.v - step, 0, 100);
      else return;
    }
    event.preventDefault(); update(next);
  }
  function edit(text: string) {
    setHex(text);
    const color = normalizeHex(text);
    if (!color) { setError('请输入有效的 HEX 颜色，例如 #EB6F4A'); return; }
    const next = hexToHsv(color);
    if (next.s === 0 || next.v === 0) next.h = current.current.h;
    current.current = next; setHsv(next); setError(''); change(color);
  }
  async function sample() {
    if (!EyeDropper || busy) return;
    const request = new AbortController(); task.current = request; setBusy(true); picking(true);
    try {
      const result = await new EyeDropper().open({ signal: request.signal });
      if (task.current !== request || request.signal.aborted) return;
      edit(result.sRGBHex);
    } catch (reason) {
      if (!request.signal.aborted && !(reason instanceof DOMException && reason.name === 'AbortError')) setError('取色失败，请重试');
    } finally {
      if (task.current === request && !request.signal.aborted) { task.current = null; setBusy(false); picking(false); }
    }
  }
  // 色域渐变属于用户可编辑颜色数据，不是界面 UI 的配色。
  const colors = { '--color-picker-hue': hsvToHex({ h: hsv.h, s: 100, v: 100 }), '--color-picker-white': '#FFFFFF', '--color-picker-black': '#000000', '--color-picker-spectrum': `linear-gradient(to bottom, ${[0,60,120,180,240,300,360].map(h => hsvToHex({ h, s:100, v:100 })).join(',')})` } as CSSProperties;
  return <div aria-label={`${label}选色面板`} className={styles.panel} role="group" style={colors}>
    <div className={styles.main}>
      <div aria-label="饱和度和明度" aria-valuemax={100} aria-valuemin={0} aria-valuenow={Math.round(hsv.s)} aria-valuetext={`饱和度 ${Math.round(hsv.s)}%，明度 ${Math.round(hsv.v)}%；左右调整饱和度，上下调整明度`} className={styles.sv} onKeyDown={event => keyboard(event, false)} onPointerDown={event => { event.currentTarget.focus(); event.currentTarget.setPointerCapture(event.pointerId); point(event, false); }} onPointerMove={event => { if (event.currentTarget.hasPointerCapture(event.pointerId)) point(event, false); }} role="slider" tabIndex={0}>
        <span className={styles.svThumb} style={{ left: `${hsv.s}%`, top: `${100-hsv.v}%` }} />
      </div>
      <div aria-label="色相" aria-orientation="vertical" aria-valuemax={360} aria-valuemin={0} aria-valuenow={Math.round(hsv.h)} className={styles.hue} onKeyDown={event => keyboard(event, true)} onPointerDown={event => { event.currentTarget.focus(); event.currentTarget.setPointerCapture(event.pointerId); point(event, true); }} onPointerMove={event => { if (event.currentTarget.hasPointerCapture(event.pointerId)) point(event, true); }} role="slider" tabIndex={0}>
        <span className={styles.hueThumb} style={{ top: `${hsv.h/360*100}%` }} />
      </div>
    </div>
    <div className={styles.footer}>
      <div className={styles.colorInput}><Input ariaLabel="HEX 颜色值" className={styles.hex} error={Boolean(error)} onPressEnter={() => { if (!error) close(); }} onValueChange={edit} size="small" value={hex} />
      <Button aria-busy={busy || undefined} aria-label="吸管取色" className={styles.eyedropper} disabled={!EyeDropper || busy} onClick={() => { void sample(); }} size="small" title={EyeDropper ? '从屏幕取色，Esc 取消' : '当前浏览器不支持屏幕取色'} type="default"><Icon name="sip-fill" size="base" /></Button></div><Button disabled={Boolean(error) || busy} onClick={close} size="small" type="default">确定</Button>
    </div>
    {error ? <p className={styles.error} role="alert">{error}</p> : null}
  </div>;
}

export function ColorPicker({ ariaLabel, className, disabled, onCommit, onValueChange, value }: ColorPickerProps) {
  const [open, setOpen] = useState(false);
  const edited = useRef(false);
  const draft = useRef(value);
  const sampling = useRef(false);
  const active = useRef(false);
  function toggle(next: boolean) {
    if (disabled || (!next && sampling.current)) return;
    if (next) { edited.current = false; draft.current = value; active.current = true; }
    else { if (!active.current) return; active.current = false; }
    setOpen(next);
    if (!next && edited.current) onCommit?.(draft.current);
  }
  return <Popover className={styles.popup} content={<ColorPanel change={next => { if (normalizeHex(next) !== normalizeHex(draft.current)) edited.current = true; draft.current = next; onValueChange(next); }} close={() => toggle(false)} key={open ? 'open' : 'closed'} label={ariaLabel} picking={next => { sampling.current = next; }} value={value} />} onOpenChange={toggle} open={open}>
    <Button aria-label={ariaLabel} className={[styles.trigger, className].filter(Boolean).join(' ')} disabled={disabled} type="default"><span className={styles.swatch} style={{ backgroundColor: value }} /></Button>
  </Popover>;
}
